import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'

// Las piezas con las que se escriben los DOS desplegables de una operación: el de Workflow (lo
// teórico) y el de Progress (lo real). Vivían dentro de `OperationsBlock.jsx`; al repartirse el
// contenido entre los dos bloques (plan «Workflow teórico · Progress real», 23/09) salen aquí para
// que los dos desplegables sigan escribiendo un dato de la MISMA forma. Dos copias acabarían
// pareciéndose cada vez menos.

// Un dato con su rótulo. `component='div'`: MUI mapea `body2` a <p>, y este hueco recibe children
// arbitrarios — la casilla de EXTERNO (E24) mete un Stack, que es un <div>. Un <div> dentro de un <p>
// no es solo un aviso de React: el navegador CIERRA el <p> antes de tiempo al parsear.
export const ExpansionDatum = ({ label, testId, children }) => (
  <Box>
    <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography component='div' data-testid={testId} variant='body2' sx={{ fontWeight: 700 }}>{children}</Typography>
  </Box>
)

// ADDENDUM 13 (E19): lo que separa significados no son las etiquetas sino la AGRUPACIÓN — un título
// de sección dice de qué familia es lo de debajo, y dentro de la familia basta el adjetivo. Cada
// sección lleva su propia rejilla, así ninguna celda se estrecha por culpa de otra.
export const ExpansionSection = ({ title, columns = 4, children }) => (
  <Box>
    {!!title && (
      <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
        {title}
      </Typography>
    )}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${columns}, 1fr)` }, gap: 2 }}>
      {children}
    </Box>
  </Box>
)

// Una lista de personas, no líneas sueltas indistinguibles del resto del panel.
export const PeopleChips = ({ testIdPrefix, people, emptyTestId, emptyLabel, keyOf, labelOf }) => (
  people.length === 0
    ? <Typography data-testid={emptyTestId} variant='body2' sx={{ color: 'text.secondary' }}>{emptyLabel}</Typography>
    : (
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
        {people.map((person) => (
          <Chip
            key={keyOf(person)}
            data-testid={`${testIdPrefix}-${keyOf(person)}`}
            size='small'
            label={labelOf(person)}
            sx={{ fontWeight: 700 }}
          />
        ))}
      </Stack>
      )
)

/**
 * S426 / S432 — las PESTAÑAS de un desplegable de operación, cada una CON SU RECUENTO.
 *
 * ⚠️ Solo se monta la pestaña ABIERTA. Es la misma decisión que el plegable de S385: montar las
 * demás ocultas con `display: none` fue un defecto de este bloque —datos en divs invisibles que solo
 * veían los tests—, y un test que tiene que pulsar la pestaña comprueba lo que de verdad se lee.
 * ⚠️ El recuento va EN la pestaña y con su número siempre, también el cero: «Reportes 0» es un dato
 * medido. Lo que NO se hace es pintar una pestaña cuyo contenido el servidor aún no sirve — un «0»
 * ahí afirmaría que la operación no tiene tareas cuando lo que pasa es que nadie lo ha preguntado.
 * Por eso las pestañas llegan ya decididas por quien sabe qué datos hay.
 *
 * @param {{testId: string, tabs: Array<{key: string, label: string, count: number, content: React.ReactNode}>}} props
 */
export const DetailTabs = ({ testId, tabs }) => {
  const [active, setActive] = useState(tabs.length > 0 ? tabs[0].key : '')
  const current = tabs.find((tab) => tab.key === active) || tabs[0]
  if (!current) return null
  return (
    <Box data-testid={testId}>
      <Tabs
        value={current.key}
        onChange={(event, key) => setActive(key)}
        variant='scrollable'
        scrollButtons={false}
        sx={{ minHeight: 36, borderBottom: 1, borderColor: 'divider', mb: 1.5 }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            data-testid={`${testId}-tab-${tab.key}`}
            data-count={String(tab.count)}
            label={`${tab.label} · ${tab.count}`}
            sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontWeight: 700 }}
          />
        ))}
      </Tabs>
      <Box data-testid={`${testId}-panel-${current.key}`}>{current.content}</Box>
    </Box>
  )
}
